import { apiClientV2, unwrapV2Response } from './index.js';

/**
 * Adds the annotation polygons to the detections.geojson files on s3
 *
 * @async
 * @param {Object} params - The parameters for adding an annotation.
 * @param {string} params.crs - The Coordinate system.
 * @param {string} params.projectUuid - The UUID of the project.
 * @param {string} params.filename - The name of the file to add the annotation.
 * @param {string} params.modelUuid - The UUID of the model.
 * @param {Array|Object} params.polygons - The normalized polygon data for the annotations.
 * @returns {Promise<Object>} The data returned from the API.
 */
export const addAnnotations = async ({ crs, projectUuid, filename, modelUuid, polygons }) => {
	// TODO: when the v2 is removed from the backend, update this
	const { data, error } = await apiClientV2.post(`/annotations/features`, {
		crs,
		project_uuid: projectUuid,
		model_uuid: modelUuid,
		filename,
		polygons,
	});

	if (error) console.error({ error });

	return data;
};

/**
 * Deletes annotations from the detections.geojson files on s3
 *
 * @async
 * @param {Object} params - The parameters for deleting an annotation
 * @param {string} params.projectUuid - UUID of the project
 * @param {string} params.modelUuid - UUID of the model
 * @param {Array} params.annotationUuids - UUID of the annotations to be deleted
 * @returns {Promise<Object>} The data returned from the API.
 */
export const deleteAnnotations = async ({ projectUuid, modelUuid, annotationUuids }) => {
	// TODO: when the v2 is removed from the backend, update this
	const { data, error } = await apiClientV2.delete(`/annotations/features`, {
		project_uuid: projectUuid,
		model_uuid: modelUuid,
		annotation_ids: annotationUuids,
	});

	if (error) console.error(JSON.stringify(error, null, 2));

	return data;
};

/**
 * Per-node annotation summary for the caller's taxonomy: how many examples
 * each node has, split by free-text label and by project, and the server's
 * answer on whether a child object can be trained from it, plus the thresholds
 * every node is held to.
 *
 * v2 endpoint: `GET taxonomy/annotations/summary`.
 * Data: `{ nodes, required_number_of_annotations, required_number_of_tiles }`.
 * Node shape:
 *   {
 *     node_uuid, key,
 *     total_number_of_annotations, total_number_of_tiles,
 *     total_number_of_background_annotations,
 *     can_be_trained,   // enough tiles and annotations, and new rows since the last training
 *     labels:   [{ label: string | null, total_number_of_annotations,
 *                  total_number_of_tiles, can_be_trained }],   // null = unlabelled, last
 *     projects: [{ project_uuid, total_number_of_annotations, total_number_of_tiles,
 *                  total_number_of_background_annotations, labels }],
 *   }
 *
 * @returns {Promise<{ nodes: Array, required_number_of_annotations: number | null,
 *   required_number_of_tiles: number | null }>}
 */
export const getAnnotationSummary = async () => {
	const { data, error } = await apiClientV2.get('/taxonomy/annotations/summary');
	if (error) throw error;
	const summary = unwrapV2Response(data);
	return {
		nodes: summary?.nodes ?? [],
		required_number_of_annotations: summary?.required_number_of_annotations ?? null,
		required_number_of_tiles: summary?.required_number_of_tiles ?? null,
	};
};

/**
 * The annotation list of one object in a project: model boxes no correction
 * hides plus the annotators' `add` / `edit` rows.
 *
 * v2 endpoint: `POST /projects/{project}/nodes/{node}/annotations`.
 *
 * @param {object} params
 * @param {string} params.projectUuid
 * @param {string} params.nodeUuid
 * @param {'3857'|'normalized'} [params.projection]
 * @param {Array<[number, number]>} [params.aoi] - ring in EPSG:3857 (at least 3 points)
 * @param {'model'|'human'|null} [params.source]
 * @param {boolean} [params.includeRejects]
 * @param {boolean} [params.includeBackground]
 * @param {boolean} [params.includeEditTargets]
 * @returns {Promise<{ type: 'FeatureCollection', features: Array, status: string }>}
 */
export const postNodeAnnotations = async ({
	projectUuid,
	nodeUuid,
	projection,
	aoi,
	source,
	includeRejects,
	includeBackground,
	includeEditTargets,
}) => {
	const body = {};
	if (projection) body.projection = projection;
	if (aoi?.length) body.aoi = aoi;
	if (source !== undefined) body.source = source;
	if (includeRejects !== undefined) body.include_rejects = includeRejects;
	if (includeBackground !== undefined) body.include_background = includeBackground;
	if (includeEditTargets !== undefined) body.include_edit_targets = includeEditTargets;

	const { data, error } = await apiClientV2.post(
		`/projects/${projectUuid}/nodes/${nodeUuid}/annotations`,
		body
	);
	if (error) throw error;
	return unwrapV2Response(data);
};

/**
 * Replace every row of one object on one tile. An empty `annotations` list stores
 * an empty mark. Corrections of model boxes travel in the same save.
 *
 * v2 endpoint: `PUT /projects/{project}/nodes/{node}/annotations/tiles`.
 *
 * @param {object} params
 * @param {string} params.projectUuid
 * @param {string} params.nodeUuid
 * @param {string} params.tileName
 * @param {'3857'|'normalized'} params.projection
 * @param {Array<{ action: 'add'|'edit'|'reject', annotation_type: string, geometry: object,
 *   label?: string, target_geometry?: object }>} params.annotations
 * @returns {Promise<{ annotation_uuids: string[] }>} the stored ids, in request order
 */
export const putTileAnnotations = async ({
	projectUuid,
	nodeUuid,
	tileName,
	projection,
	annotations,
}) => {
	const { data, error } = await apiClientV2.put(
		`/projects/${projectUuid}/nodes/${nodeUuid}/annotations/tiles`,
		{ tile_name: tileName, projection, annotations }
	);
	if (error) throw error;
	return unwrapV2Response(data);
};
